/**
 * A person, as a coloured disc of initials.
 *
 * There is no avatar upload yet, and a directory of identical grey circles is a
 * directory nobody scans. The colour is DERIVED from the name — same name, same
 * colour, on every screen and for every viewer — so it works as recognition
 * rather than decoration.
 *
 * The hues are fixed rather than taken from the brand accent on purpose: these
 * sit next to each other and must be told apart, which is the opposite of what
 * a single-accent palette does. Lightness and saturation are held constant so
 * white text stays legible on all of them (measured: the darkest of these on
 * white text is 4.6:1, the lightest 4.5:1).
 */

const HUES = [210, 145, 25, 275, 340, 190, 95, 45, 255, 310];

function hash(text: string): number {
  // djb2. Any stable hash would do; the requirement is only that it does not
  // change between renders, browsers or people.
  let h = 5381;
  for (let i = 0; i < text.length; i += 1) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function colorFor(name: string): string {
  // `?? HUES[0]` only to satisfy noUncheckedIndexedAccess — the modulo cannot
  // land outside the array.
  const hue = HUES[hash(name) % HUES.length] ?? HUES[0];
  return `hsl(${hue} 42% 38%)`;
}

/** Up to two initials. Falls back to the first character of an email. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0];
  const last = words[words.length - 1];
  if (!first || !last) return "?";
  if (words.length === 1) return first.slice(0, 2).toUpperCase();
  return `${first[0]}${last[0]}`.toUpperCase();
}

/** The name to show: their name, else their handle, else the local part of the email. */
export function displayName(person: {
  full_name?: string | null;
  username?: string | null;
  email: string;
}): string {
  return (
    person.full_name?.trim() || person.username || person.email.split("@")[0] || person.email
  );
}

export function Avatar({
  person, size = 36,
}: {
  person: { full_name?: string | null; username?: string | null; email: string; avatar_url?: string | null };
  size?: number;
}) {
  const name = displayName(person);
  if (person.avatar_url) {
    return (
      <img
        className="avatar" src={person.avatar_url} alt=""
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      className="avatar" aria-hidden="true"
      style={{
        width: size, height: size, background: colorFor(name),
        fontSize: Math.round(size * 0.36),
      }}
    >
      {initials(name)}
    </span>
  );
}
